import moment from "moment";
import { Attendee, DemographicAnswer } from "../../types/attendee";
import { activeCard, cardTypeLabel } from "./cardOnFile";

const escape = (v: unknown): string => {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (s.includes(",") || s.includes('"') || s.includes("\n")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const answerText = (a: DemographicAnswer): string =>
  Array.isArray(a.answer) ? a.answer.join("; ") : a.answer ?? "";

const fmtDate = (iso?: string) => (iso ? moment(iso).format("MM/DD/YYYY h:mm a") : "");

const collectQuestions = (attendees: Attendee[]): string[] => {
  // Preserve first-seen ordering across attendees so output is stable.
  const seen: string[] = [];
  const set = new Set<string>();
  attendees.forEach((a) => {
    (a.optional_datas ?? []).forEach((q) => {
      if (!set.has(q.question)) {
        set.add(q.question);
        seen.push(q.question);
      }
    });
  });
  return seen;
};

export const exportAttendeesCsv = (attendees: Attendee[]) => {
  const questions = collectQuestions(attendees);

  const baseHeaders = [
    "ID",
    "First Name",
    "Last Name",
    "Event",
    "Phone",
    "Email",
    "Validation Code",
    "Status",
    "Card on File",
    "Card Type",
    "Note",
    "Created",
    "Updated",
  ];
  const headers = [...baseHeaders, ...questions];

  const rows: string[][] = [headers];

  attendees.forEach((a) => {
    const answers = new Map<string, string>();
    (a.optional_datas ?? []).forEach((q) => answers.set(q.question, answerText(q)));

    const status = a.is_suspended ? "Suspended" : a.is_active === false ? "Inactive" : "Active";
    const card = activeCard(a);

    const row = [
      String(a.id),
      a.first_name ?? "",
      a.last_name ?? "",
      a.event?.name ?? "",
      a.phone_number ?? "",
      a.email ?? "",
      a.validation_code ?? "",
      status,
      card ? `**** ${card.last_four_digits ?? ""}` : "",
      card ? cardTypeLabel(card) : "",
      a.note ?? "",
      fmtDate(a.created_at),
      fmtDate(a.updated_at),
      ...questions.map((q) => answers.get(q) ?? ""),
    ];

    rows.push(row);
  });

  const csv = rows.map((r) => r.map(escape).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const timestamp = new Date().toISOString().replace(/[-:]/g, "").split(".")[0];
  a.download = `Attendees_${timestamp}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};
